import { useContext, useEffect, useMemo } from 'react';
import { useSnapshot } from 'valtio';

import type { EntityReferences, VisibilityTypeId } from '#types/System.types';

type EntityReferenceValue = EntityReferences[string];

import { formStore, PageScopeContext } from '../store';
import {
    acquireEntityRecordStore,
    releaseEntityRecordStore,
    EMPTY_ENTITY_RECORD_STATE,
} from '../store/entityRecord/entityRecordStore';
import { useVisibilityMode } from './useVisibilityMode';

export type UseEntityRefScopeReturn = {
    insideForm: boolean;
    insideEntity: boolean;
    formId: string | undefined;
    versionId: string | undefined;
    entityId: string | undefined;
    recordId: string | undefined;
    entityLocked: boolean;
    visibilityMode: VisibilityTypeId;
    externalReference: EntityReferenceValue | null;
    /** All references on the active artifact — needed by writers building the merged map. */
    allExternalReferences: EntityReferences | undefined;
};

/**
 * Narrow scope reader for `useEntityRecordUI`.
 *
 * Subscribes only to the specific `entityReferences[key]` plus page identifiers,
 * avoiding the broad `useFormStore()` / `useEntityRecordScope()` subscriptions
 * that re-render every entity reference UI on any data write.
 *
 * Deliberately does NOT read `formType` — the form layout tree is orders of
 * magnitude larger than this scope and any layout edit would invalidate every
 * rendered entity reference. Writers that need the layout to resolve a grouped
 * reference key read it non-reactively at write time.
 */
export const useEntityRefScope = (referenceKey: string): UseEntityRefScopeReturn => {
    const pageScope = useSnapshot(useContext(PageScopeContext));
    const visibilityMode = useVisibilityMode();

    const formSnap = useSnapshot(formStore.state);

    const entityPreview = pageScope.source === 'preview' ? pageScope.preview : null;
    const scopedEntityId = pageScope.source === 'entity' ? pageScope.entityId : '';
    const scopedRecordId = pageScope.source === 'entity' ? pageScope.recordId : undefined;
    const isLiveEntityBound = scopedEntityId !== '';

    const entityEntry = useMemo(
        () => (isLiveEntityBound ? acquireEntityRecordStore(scopedEntityId, scopedRecordId) : null),
        [isLiveEntityBound, scopedEntityId, scopedRecordId],
    );

    useEffect(() => {
        if (!isLiveEntityBound) return;
        return () => {
            releaseEntityRecordStore(scopedEntityId, scopedRecordId);
        };
    }, [isLiveEntityBound, scopedEntityId, scopedRecordId]);

    const entitySnap = useSnapshot(entityEntry?.state ?? EMPTY_ENTITY_RECORD_STATE);

    const insideForm = formSnap.formId !== undefined;
    const insideEntity = entityPreview ? entityPreview.insideEntityRecordContext : isLiveEntityBound;

    const allRefs = entityPreview
        ? entityPreview.entityRecord.data?.entityReferences
        : isLiveEntityBound
            ? entitySnap.entityRecord.data?.entityReferences
            : formSnap.formVersion.data?.entityReferences;

    const externalReference = (allRefs?.[referenceKey] as EntityReferenceValue | undefined) ?? null;

    const entityLocked = entityPreview
        ? entityPreview.entityLocked
        : isLiveEntityBound
            ? entitySnap.entityLocked
            : false;

    return {
        insideForm,
        insideEntity,
        formId: formSnap.formId,
        versionId: formSnap.versionId,
        entityId: entityPreview?.entityId ?? (isLiveEntityBound ? scopedEntityId : undefined),
        recordId: entityPreview?.recordId ?? (isLiveEntityBound ? scopedRecordId : undefined),
        entityLocked,
        visibilityMode,
        externalReference,
        allExternalReferences: allRefs as UseEntityRefScopeReturn['allExternalReferences'],
    };
};
